import React, { useState, useEffect, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Select from "react-select";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import { useQueryClient } from "@tanstack/react-query";
import { useUsers } from "../../../hooks/useGetUsers";
import { usePostIntervention } from "../../../hooks/TICKET/useTicketInterventions";
import { toUTCISOString } from "../../../utils/date";
import MarkdownEditor from "../../MarkdownEditor/MarkdownEditor";
import { toast } from "react-toastify";
import { customSelectStyles } from "../../../utils/reactSelectStyles";
import { useAuth } from "../../../context/AuthContext";

const NewIntervention = ({ showNewIntervention, setShowNewIntervention, ticketId }) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateHeureIntervention, setDateHeureIntervention] = useState("");
  const [duree, setDuree] = useState("00:10");
  const [description, setDescription] = useState("");
  const [intervenantIds, setIntervenantIds] = useState([]);
  const queryClient = useQueryClient();
  const { data: usersData, isLoading: usersLoading } = useUsers();
  const postInterventionMutation = usePostIntervention();

  const getCurrentDateTimeLocal = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  };

  const optionsCollaborateurs = useMemo(() => {
    return usersData?.data
      ?.filter((u) => u.is_active)
      ?.map((u) => ({
        value: u.id,
        label: `${u.name || ""} ${u.surname || ""}`.trim(),
      })) || [];
  }, [usersData]);

  const selectedIntervenants = useMemo(() => {
    return optionsCollaborateurs.filter((opt) => intervenantIds.includes(opt.value));
  }, [optionsCollaborateurs, intervenantIds]);

  useEffect(() => {
    if (showNewIntervention) {
      setDateHeureIntervention(getCurrentDateTimeLocal());
      setDuree("00:10");
      setDescription("");
      setIntervenantIds(user?.data?.id ? [user.data.id] : []);
    }
  }, [showNewIntervention, user, optionsCollaborateurs]);

  const resetForm = () => {
    setDateHeureIntervention(getCurrentDateTimeLocal());
    setDuree("00:10");
    setDescription("");
    setIntervenantIds(user?.data?.id ? [user.data.id] : []);
  };

  const convertDurationToMinutes = (time) => {
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    if (intervenantIds.length === 0) {
      toast.error("Veuillez sélectionner au moins un intervenant");
      return;
    }

    const baseInterventionData = {
      description,
      date_debut: toUTCISOString(dateHeureIntervention),
      duree: convertDurationToMinutes(duree),
    };

    setIsSubmitting(true);
    try {
      const promises = intervenantIds.map((userId) => {
        const interventionData = {
          intervention: {
            ...baseInterventionData,
            utilisateur: userId,
          },
        };
        return postInterventionMutation.mutateAsync({ ticketId, interventionData });
        
      });

      await Promise.all(promises);

      queryClient.invalidateQueries({ queryKey: ["ticket", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["tickets"] });

      toast.success(`${intervenantIds.length} intervention(s) créée(s) avec succès`);
      setShowNewIntervention(false);
      resetForm();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Erreur lors de la création");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={showNewIntervention} onHide={() => setShowNewIntervention(false)} size="lg">
      <Form onSubmit={handleSubmitForm}>
        <Modal.Header closeButton>
          <Modal.Title>
            <h5 className="modal-title text-muted">Intervention sur le ticket</h5>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label className="text-muted">Date et heure intervention *</Form.Label>
              <Form.Control
                type="datetime-local"
                value={dateHeureIntervention}
                onChange={(e) => setDateHeureIntervention(e.target.value)}
                required
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label className="text-muted">Durée *</Form.Label>
              <Form.Control
                type="time"
                value={duree}
                onChange={(e) => setDuree(e.target.value)}
                required
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label className="text-muted">Compte-rendu *</Form.Label>
              <MarkdownEditor
                value={description}
                onChange={setDescription}
                rows={4}
                placeholder="Détaillez les actions réalisées (diagnostic, corrections, tests effectués…)"
                required
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label className="text-muted">Intervenants *</Form.Label>
              <Select
                options={optionsCollaborateurs}
                placeholder="Sélectionner un ou plusieurs intervenants"
                value={selectedIntervenants}
                onChange={(opts) => {
                  const ids = opts ? opts.map((o) => o.value) : [];
                  setIntervenantIds(ids);
                }}
                isLoading={usersLoading}
                isMulti
                styles={customSelectStyles}
              />
            </Form.Group>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" disabled={isSubmitting} onClick={() => setShowNewIntervention(false)}>
            Annuler
          </Button>
          <Button variant="warning" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Chargement..." : "Créer"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default NewIntervention;